/**
 * The one retry predicate for every React Query query and mutation in the app.
 *
 * WHY IT LIVES HERE AND NOT IN `App.tsx`. It is the single most
 * blast-radius-heavy function in the client — it runs on EVERY failed request,
 * and a throw inside it is an unhandled error on the already-degraded path. It
 * was previously a private const in `App.tsx`, which meant it could only be
 * exercised by mounting the entire application. It is now a module so the
 * failure shapes below are pinned by a test instead of by inspection.
 *
 * TWO DEFECTS ARE FIXED HERE, both surfaced by naming callable failures:
 *
 *  1. The old predicate branched on `'code' in error` and then called
 *     `code.startsWith('auth/')`. `CallableInvocationError` carried an own
 *     `code` property that was `undefined` whenever the rejection had no code
 *     (offline, a plain `Error`), so the branch was entered and the call threw
 *     a TypeError INSIDE the predicate. Fixed on both sides: the error no
 *     longer creates the property (see `namedCallable.ts`), and the predicate
 *     now narrows on `typeof code === 'string'` rather than on existence.
 *
 *  2. The CORS fallback read `error.message`. For a wrapped callable that
 *     message is `"<callable> failed: <code>"`, which can never contain
 *     "CORS" — so wrapping silently made CORS failures retryable again. It now
 *     reads through `callableFailureMessage`, which returns the ORIGINAL
 *     rejection's text when the error has been renamed, so behaviour for a
 *     wrapped callable equals the pre-wrapping behaviour.
 */
import { callableFailureMessage } from './telemetry/namedCallable';

/**
 * Firebase Functions error codes that indicate permanent failures (no retry).
 */
export const NON_RETRYABLE_CODES = new Set([
  'functions/unauthenticated',
  'functions/permission-denied',
  'functions/not-found',
  'functions/invalid-argument',
  'functions/already-exists',
  'functions/resource-exhausted',
  // A precondition failure is a statement about the request, not the network:
  // retrying re-runs the whole server-side check and fails identically. Left
  // retryable, sendCRMEmail's "that file isn't shared with the recipient"
  // rejection would run three times per click.
  'functions/failed-precondition',
]);

/** The rejection's error code, when it has one that is actually a string. */
function codeOf(error: unknown): string | undefined {
  if (!error || typeof error !== 'object') return undefined;
  const code = (error as { code?: unknown }).code;
  return typeof code === 'string' ? code : undefined;
}

/**
 * Returns true for errors that should never be retried (auth, 4xx, CORS).
 *
 * MUST NOT THROW for any input. It runs inside React Query's retry callback,
 * where a throw is an unhandled error on a path that is already failing.
 */
export const isNonTransientError = (error: unknown): boolean => {
  const code = codeOf(error);
  if (code !== undefined) {
    if (NON_RETRYABLE_CODES.has(code)) return true;
    // Auth errors from firebase/auth
    if (code.startsWith('auth/')) return true;
  }
  // Fallback for non-Firebase errors (CORS). Read THROUGH the rename so a
  // wrapped callable classifies the same as an unwrapped one.
  return callableFailureMessage(error).includes('CORS');
};

/**
 * The React Query defaults the whole app runs with (App.tsx passes this
 * object to its QueryClient; a test pins it).
 *
 * MUTATIONS NEVER AUTO-RETRY (2026-09-28). A mutation is a write or a send.
 * The previous default retried any "transient" failure up to twice, and a
 * client timeout counts as transient, so one slow write could run three
 * times. Measured: one click on a CRM send became THREE customer emails once
 * a cold server warmed up (16:33Z, messageIds ...a869 / ...2111 / ...89aa).
 * The same default covered SMS, invoice and payment actions. A timed-out
 * write may already have succeeded on the server; only the person, or a
 * server-side idempotency key, may decide to try again.
 *
 * Opting in: a mutation PROVEN idempotent (safe to run twice) may set its own
 * `retry`, with a comment saying why. None do today (census 2026-09-28: every
 * explicit `retry: n > 0` in client/src is on a query, i.e. a read).
 *
 * Queries (reads) keep one retry for transient errors: re-reading is safe.
 */
export const QUERY_CLIENT_DEFAULTS = {
  queries: {
    refetchOnWindowFocus: false,
    retry: (failureCount: number, error: unknown): boolean => {
      if (isNonTransientError(error)) return false;
      // Retry transient errors (500, timeout) once
      return failureCount < 1;
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 30, // 30 minutes
  },
  mutations: {
    retry: false,
  },
} as const;
